/**
 * ProcessBar Component
 *
 * Renders a single batch's production process as a horizontal bar with phase segments.
 * Each phase shows as a coloured segment whose opacity reflects completion status:
 *   - pending: 0.3 opacity (translucent)
 *   - active: 0.6 opacity (semi-opaque)
 *   - completed: 1.0 opacity (fully opaque)
 *
 * Supports two display modes:
 *   - Contiguous: phases shown as a continuous sausage, widths from planned durations
 *   - Non-contiguous: phases positioned by actual dates, gaps shown honestly
 */

import React, { useState } from 'react'
import ReactDOM from 'react-dom'
import { cn } from '@/lib/utils'

// Phase colours by process type (matches StrainProcessesSwimlane pattern)
const PROCESS_TYPE_COLORS = {
  liquid_culture: { bg: '#8b5cf6', text: '#fff' },  // purple
  spawn:          { bg: '#f59e0b', text: '#fff' },  // amber
  block:          { bg: '#10b981', text: '#fff' },  // emerald
  cultivation:    { bg: '#3b82f6', text: '#fff' },  // blue
}

// Map phase names to process types for colouring
const PHASE_TO_PROCESS_TYPE = {
  // Liquid Culture phases (purple)
  'Liquid Culture': 'liquid_culture',
  'LC Incubation': 'liquid_culture',
  'Culture Prep': 'liquid_culture',
  // Spawn phases (amber)
  'Spawn Production': 'spawn',
  'Spawn Run': 'spawn',
  'Spawn Incubation': 'spawn',
  'Spawn Colonisation': 'spawn',
  'Colonisation': 'spawn',
  // Block Production phases (green)
  'Block Incubation': 'block',
  'Substrate Incubation': 'block',
  'Block Production': 'block',
  // Cultivation phases (blue)
  'Incubation': 'cultivation',
  'Primordia Induction': 'cultivation',
  'Primordia': 'cultivation',
  'Pinning': 'cultivation',
  'Fruiting': 'cultivation',
  'First Flush': 'cultivation',
  'Flush 1': 'cultivation',
  'Second Flush': 'cultivation',
  'Flush 2': 'cultivation',
  'Third Flush': 'cultivation',
  'Flush 3': 'cultivation',
  'Flush Interval': 'cultivation',
  'Rest Period': 'cultivation',
  'Rest': 'cultivation',
}

// Opacity by phase status
const STATUS_OPACITY = {
  pending: 0.3,
  active: 0.6,
  completed: 1.0,
  skipped: 0.15,
}

// Resolve process type: use DB value if phases span multiple process types,
// otherwise fall back to name-based mapping (handles single-process strains
// where all phases have the same process_type from the DB)
function resolveProcessType(phase, useDbType = true) {
  if (useDbType && phase.processType) return phase.processType
  return PHASE_TO_PROCESS_TYPE[phase.name] || PHASE_TO_PROCESS_TYPE[phase.label] || 'cultivation'
}

// Check if DB process types provide meaningful grouping (more than one distinct type)
function hasDistinctDbProcessTypes(phases) {
  const dbTypes = new Set(phases.map(p => p.processType).filter(Boolean))
  return dbTypes.size > 1
}

// Group consecutive phases by process type for bracket annotations
function groupPhasesByProcessType(phases) {
  const useDbType = hasDistinctDbProcessTypes(phases)
  const groups = []
  let current = null

  phases.forEach(phase => {
    const processType = resolveProcessType(phase, useDbType)
    if (!current || current.processType !== processType) {
      current = {
        processType,
        phases: [phase],
        totalDays: phase.duration || phase.actualDuration || 0,
      }
      groups.push(current)
    } else {
      current.phases.push(phase)
      current.totalDays += phase.duration || phase.actualDuration || 0
    }
  })

  return groups
}

const PROCESS_TYPE_LABELS = {
  liquid_culture: 'Liquid Culture',
  spawn: 'Spawn Production',
  block: 'Block Production',
  cultivation: 'Block Cultivation',
}

/**
 * ProcessBar — horizontal phase bar for a single batch
 */
export default function ProcessBar({ phases, mode = 'contiguous', height = 40, className = '', onPhaseClick }) {
  const [hoveredPhase, setHoveredPhase] = useState(null)
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 })

  if (!phases || phases.length === 0) {
    return (
      <div className={cn('h-10 bg-muted/30 rounded-lg flex items-center justify-center text-sm text-muted-foreground', className)}>
        No phases defined
      </div>
    )
  }

  const handleMouseEnter = (phase, e) => {
    const rect = e.currentTarget.getBoundingClientRect()
    setPopupPosition({
      x: rect.left + rect.width / 2,
      y: rect.top,
    })
    setHoveredPhase(phase)
  }

  const handleMouseLeave = () => {
    setHoveredPhase(null)
  }

  // Group phases for bracket annotations
  const processGroups = groupPhasesByProcessType(phases)
  const totalDays = phases.reduce((sum, p) => sum + (p.duration || p.actualDuration || 0), 0)

  if (mode === 'non-contiguous') {
    return (
      <NonContiguousBar
        phases={phases}
        processGroups={processGroups}
        height={height}
        className={className}
        hoveredPhase={hoveredPhase}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        popupPosition={popupPosition}
        onPhaseClick={onPhaseClick}
      />
    )
  }

  // Contiguous mode — widths from planned durations
  if (totalDays === 0) {
    return (
      <div className={cn('h-10 bg-muted/30 rounded-lg flex items-center justify-center text-sm text-muted-foreground', className)}>
        No phase durations defined
      </div>
    )
  }

  // Calculate bracket widths from process groups
  const bracketWidths = processGroups.map(g => ({
    processType: g.processType,
    label: PROCESS_TYPE_LABELS[g.processType] || 'Other',
    days: g.totalDays,
    widthPercent: (g.totalDays / totalDays) * 100,
  })).filter(b => b.days > 0)

  // Track shade variation within each process type
  const processTypeCounters = {}

  return (
    <div className={cn('relative', className)}>
      {/* Top brackets with labels */}
      <div className="flex mb-1 overflow-visible">
        {bracketWidths.map((t, i) => (
          <div key={`${t.processType}-${i}`} style={{ width: `${t.widthPercent}%` }} className="px-0.5 overflow-visible relative">
            <div className="absolute left-1/2 -translate-x-1/2 text-xs text-foreground mb-0.5 whitespace-nowrap">
              {t.label}
            </div>
            <div className="h-4" />
            <div
              style={{
                borderLeft: '1px solid currentColor',
                borderRight: '1px solid currentColor',
                borderTop: '1px solid currentColor',
                borderRadius: '4px 4px 0 0',
                height: '6px',
              }}
              className="text-border"
            />
          </div>
        ))}
      </div>

      {/* Main process bar */}
      <div
        className="flex rounded-lg overflow-hidden border border-border/50 bg-muted/20"
        style={{ height }}
      >
        {phases.map((phase, index) => {
          const duration = phase.duration || phase.actualDuration || 0
          if (duration === 0) return null

          // Use per-phase colour from data (useTimeline PHASE_COLORS) when available,
          // otherwise fall back to process-type colour
          const processType = resolveProcessType(phase)
          const fallbackColors = PROCESS_TYPE_COLORS[processType] || PROCESS_TYPE_COLORS.cultivation
          const bgColor = phase.color || fallbackColors.bg
          const textColor = phase.textColor || fallbackColors.text
          const opacity = STATUS_OPACITY[phase.status] || 0.3

          const width = (duration / totalDays) * 100
          const showLabel = width > 10

          return (
            <div
              key={`${phase.phaseId || phase.name}-${index}`}
              className="relative flex items-center justify-center text-xs font-medium overflow-hidden transition-all hover:brightness-110 cursor-pointer border-r border-white/20 last:border-r-0"
              style={{
                width: `${width}%`,
                backgroundColor: bgColor,
                opacity,
                color: textColor,
                textShadow: '0 1px 2px rgba(0,0,0,0.3)',
              }}
              onMouseEnter={(e) => handleMouseEnter(phase, e)}
              onMouseLeave={handleMouseLeave}
              onClick={(e) => onPhaseClick?.(phase, e)}
            >
              {showLabel && (
                <span className="truncate px-1 text-[10px]">
                  {phase.label || phase.name}
                </span>
              )}
            </div>
          )
        })}
      </div>

      {/* Bottom brackets with durations */}
      <div className="flex mt-1">
        {bracketWidths.map((t, i) => (
          <div key={`${t.processType}-bottom-${i}`} style={{ width: `${t.widthPercent}%` }} className="px-0.5">
            <div
              style={{
                borderLeft: '1px solid currentColor',
                borderRight: '1px solid currentColor',
                borderBottom: '1px solid currentColor',
                borderRadius: '0 0 4px 4px',
                height: '6px',
              }}
              className="text-border"
            />
            <div className="text-center text-xs text-muted-foreground mt-0.5">
              {t.days}d
            </div>
          </div>
        ))}
      </div>

      {/* Hover popup */}
      <PhaseTooltip phase={hoveredPhase} position={popupPosition} />
    </div>
  )
}

/**
 * Non-contiguous mode — phases positioned by actual dates with gaps shown
 */
function NonContiguousBar({ phases, processGroups, height, className, hoveredPhase, onMouseEnter, onMouseLeave, popupPosition, onPhaseClick }) {
  // Find the overall date range from actual or planned dates
  const dates = phases.flatMap(p => {
    const start = p.actualStart || p.startDate
    const end = p.actualEnd || p.endDate
    return [start, end].filter(Boolean)
  })

  if (dates.length === 0) {
    return (
      <div className={cn('h-10 bg-muted/30 rounded-lg flex items-center justify-center text-sm text-muted-foreground', className)}>
        No date data available
      </div>
    )
  }

  const rangeStart = new Date(Math.min(...dates.map(d => d.getTime())))
  const rangeEnd = new Date(Math.max(...dates.map(d => d.getTime())))
  const totalMs = rangeEnd.getTime() - rangeStart.getTime()

  if (totalMs <= 0) {
    return (
      <div className={cn('h-10 bg-muted/30 rounded-lg flex items-center justify-center text-sm text-muted-foreground', className)}>
        Insufficient date range
      </div>
    )
  }

  // Calculate bracket positions from process groups using actual dates
  const groupDateRanges = processGroups.map(g => {
    const groupDates = g.phases.flatMap(p => {
      const start = p.actualStart || p.startDate
      const end = p.actualEnd || p.endDate
      return [start, end].filter(Boolean)
    })
    const groupStart = new Date(Math.min(...groupDates.map(d => d.getTime())))
    const groupEnd = new Date(Math.max(...groupDates.map(d => d.getTime())))
    const leftPercent = ((groupStart.getTime() - rangeStart.getTime()) / totalMs) * 100
    const widthPercent = ((groupEnd.getTime() - groupStart.getTime()) / totalMs) * 100
    const days = Math.round((groupEnd.getTime() - groupStart.getTime()) / (1000 * 60 * 60 * 24))
    return {
      processType: g.processType,
      label: PROCESS_TYPE_LABELS[g.processType] || 'Other',
      leftPercent,
      widthPercent: Math.max(widthPercent, 1), // minimum 1%
      days,
    }
  })

  const processTypeCounters = {}

  return (
    <div className={cn('relative', className)}>
      {/* Top brackets */}
      <div className="relative h-8 mb-1">
        {groupDateRanges.map((g, i) => (
          <div
            key={`${g.processType}-${i}`}
            className="absolute overflow-visible"
            style={{ left: `${g.leftPercent}%`, width: `${g.widthPercent}%` }}
          >
            <div className="absolute left-1/2 -translate-x-1/2 text-xs text-foreground whitespace-nowrap">
              {g.label}
            </div>
            <div className="h-4" />
            <div
              style={{
                borderLeft: '1px solid currentColor',
                borderRight: '1px solid currentColor',
                borderTop: '1px solid currentColor',
                borderRadius: '4px 4px 0 0',
                height: '6px',
              }}
              className="text-border"
            />
          </div>
        ))}
      </div>

      {/* Main bar — phases positioned absolutely */}
      <div
        className="relative rounded-lg border border-border/50 bg-muted/10"
        style={{ height }}
      >
        {phases.map((phase, index) => {
          const start = phase.actualStart || phase.startDate
          const end = phase.actualEnd || phase.endDate
          if (!start || !end) return null

          const leftPercent = ((start.getTime() - rangeStart.getTime()) / totalMs) * 100
          const widthPercent = Math.max(((end.getTime() - start.getTime()) / totalMs) * 100, 0.5)

          const processType = resolveProcessType(phase)
          const fallbackColors = PROCESS_TYPE_COLORS[processType] || PROCESS_TYPE_COLORS.cultivation
          const bgColor = phase.color || fallbackColors.bg
          const textColor = phase.textColor || fallbackColors.text
          const opacity = STATUS_OPACITY[phase.status] || 0.3

          const showLabel = widthPercent > 8

          return (
            <div
              key={`${phase.phaseId || phase.name}-${index}`}
              className="absolute top-0 bottom-0 flex items-center justify-center text-xs font-medium overflow-hidden transition-all hover:brightness-110 cursor-pointer first:rounded-l-lg last:rounded-r-lg"
              style={{
                left: `${leftPercent}%`,
                width: `${widthPercent}%`,
                backgroundColor: bgColor,
                opacity,
                color: textColor,
                textShadow: '0 1px 2px rgba(0,0,0,0.3)',
              }}
              onMouseEnter={(e) => onMouseEnter(phase, e)}
              onMouseLeave={onMouseLeave}
              onClick={(e) => onPhaseClick?.(phase, e)}
            >
              {showLabel && (
                <span className="truncate px-1 text-[10px]">
                  {phase.label || phase.name}
                </span>
              )}
            </div>
          )
        })}
      </div>

      {/* Bottom brackets */}
      <div className="relative h-7 mt-1">
        {groupDateRanges.map((g, i) => (
          <div
            key={`${g.processType}-bottom-${i}`}
            className="absolute"
            style={{ left: `${g.leftPercent}%`, width: `${g.widthPercent}%` }}
          >
            <div
              style={{
                borderLeft: '1px solid currentColor',
                borderRight: '1px solid currentColor',
                borderBottom: '1px solid currentColor',
                borderRadius: '0 0 4px 4px',
                height: '6px',
              }}
              className="text-border"
            />
            <div className="text-center text-xs text-muted-foreground mt-0.5">
              {g.days}d
            </div>
          </div>
        ))}
      </div>

      {/* Hover popup */}
      <PhaseTooltip phase={hoveredPhase} position={popupPosition} />
    </div>
  )
}

/**
 * Shared tooltip for phase hover
 */
function PhaseTooltip({ phase, position }) {
  if (!phase) return null

  const processType = resolveProcessType(phase)
  const duration = phase.actualDuration || phase.duration || 0
  const statusLabel = phase.status ? phase.status.charAt(0).toUpperCase() + phase.status.slice(1) : 'Unknown'

  return ReactDOM.createPortal(
    <div
      className="fixed shadow-lg rounded-lg border border-border px-3 py-2 text-sm pointer-events-none"
      style={{
        left: position.x,
        top: position.y - 16,
        transform: 'translate(-50%, -100%)',
        zIndex: 99999,
        backgroundColor: 'white',
        color: '#1f2937',
      }}
    >
      <div className="font-semibold">{phase.label || phase.name}</div>
      <div className="text-xs" style={{ color: '#6b7280' }}>
        {PROCESS_TYPE_LABELS[processType] || 'Other'} · {duration} days · {statusLabel}
      </div>
      {phase.actualStart && (
        <div className="text-xs mt-1" style={{ color: '#9ca3af' }}>
          {phase.actualStart.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
          {phase.actualEnd && ` — ${phase.actualEnd.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`}
        </div>
      )}
    </div>,
    document.body
  )
}
